import { DynamicFormRef } from "@mavichpe/dynamic-forms";
import { useEffect, useRef, useState } from "react";
import SettlementFormConfig from "./form";
import styles from "./style.module.scss";
import { SettlementFormData, SettlementRelatedAttributes } from "@/formTypes/SettlementDataType";
import GenericForm, { ConfirmationConfig } from "../GenericForm";
import { SettlementApi, SettlementDataType } from "@/api/SettlementApi";
import QuickAddProductForm from "../QuickAddProductForm";
import { Col, Row } from "reactstrap";
import PriceSelector from "@components/dynamic/PriceSelector";
import { PriceApi } from "@/api/PricesApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectDocumentSummary, selectEDocumentSummary } from "@/store/slices/utils/eDocumentSelectors";
import { FOUR_PRICES_MANAGEMENT_CODE, INVOICE_CONTADO_TYPE_CODE, ON_INVOICE_DISCOUNT_TYPE_CODE, CASH_PAYMENT_WAY_CODE } from "@utils/Constants";
import { addPaymentWay, clearDocument, clearPaymentWays, loadProducts, setAllProductsExemptionData, setDefaultExemption, setDocumentComments, setDocumentDiscount, setDocumentDiscountType, setDocumentExempted, setPrice, setPriceCode, setProductExemptionData } from "@/store/slices/eDocumentSlides";
import { CurrencyApi } from "@/api/CurrencyApi";
import { setCurrency } from "@/store/slices/currencySlides";
import LoadingScreen from "@components/content/LoadingScreen";
import EdocumentProductList from "@components/dynamic/EdocumentProductList";
import { ClientApi, ClientDataType } from "@/api/ClientApi";
import { showAlert } from "@utils/Alert";
import { isNaN } from "mathjs";
import { omitProperties, removeFunctionFromObject, stripHtmlTags } from "@utils/Utils";
import { AddClientToDocumentPubSub, AddClientToDocumentPubSubPayload, ConfirmPrintPubSub, CurrencyIdPubSub, LoadInvoiceTemplatePubSub, LoadInvoiceTemplatePubSubPayload } from "@utils/pubsubs/PubSub";
import { flushDocumentProductEdits } from "@utils/eDocumentFlush";
import EdocumentComments from "@components/dynamic/EdocumentComments";
import DocumentPaymentWaysForm from "../DocumentPaymentWaysForm";
import { HiddenableContainer } from "@components/layout/HiddenableContainer";
import { EDocumentPaymentWayDataType } from "@/formTypes/EDocumentPaymentWayDataType";
import EdocumentPaymentWayList from "@components/dynamic/EdocumentPaymentWayList";
import DocumentExemptionOffCanvas from "@components/layout/Offcanvas/DocumentExemptionOffCanvas";
import { ProductExemptionDataType } from "@/formTypes/ProductExemptionDataType";
import { getCurrentDateTime } from "@utils/Api";
import { SettlementPaymentDataType } from "@/api/SettlementPaymentApi";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { SettlementFormDataKeys } from "@/generated/SettlementFormDataKeys";
import { SettlementProductDataType } from "@/api/SettlementProductApi";
import DocumentPrintConfirmation from "@components/content/DocumentPrintConfirmation";
import SearchClientByIdentificationOffCanvas from "@components/layout/Offcanvas/SearchClientByIdentificationOffCanvas";
import { loadReferenceFormLabel } from "@utils/Forms";
import { shouldCapturePaymentWays } from "@utils/SaleCondition";
import { resolveDocumentClientNames } from "@utils/documentClientSnapshot";

type SettlementFormProps = {
  id?: string;
  copyData?: boolean
  formId: string,
  quoteId?: string,
  saveRedirectUrl: string
}


const SettlementForm = (props: SettlementFormProps) => {
  const dispatch = useAppDispatch()
  const { copyData = false, id: preLoadedSettlementId } = props
  const formRef = useRef<DynamicFormRef>(null);

  const [defaultData, setDefaultData] = useState<SettlementDataType>({});
  const [clients, setClients] = useState<ClientDataType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const suscriptionData = useAppSelector((state) => state.suscription.suscription);
  const documentCurrency = useAppSelector((state) => state.currency);
  const documentSummary = useAppSelector(selectDocumentSummary);
  const [documentType, setDocumentType] = useState<string>("0");
  const [formConfig, setFormConfig] = useState<any>();
  const noAutoCancelContado = suscriptionData?.no_auto_cancel_contado || false;
  const capturePaymentWays = shouldCapturePaymentWays(documentType, noAutoCancelContado);

  useEffect(() => {
    setIsLoading(true)
    loadFormConfig()
    loadDefaultData()
  }, [suscriptionData])

  useEffect(() => {
    const unsubscribe = AddClientToDocumentPubSub.subscribe(addClientToDocument);
    const unsubscribeInvoiceTemplate = LoadInvoiceTemplatePubSub.subscribe(loadInvoiceTemplate);
    dispatch(clearDocument())
    return () => {
      unsubscribe();
      unsubscribeInvoiceTemplate();
    };
  }, []);

  const loadFormConfig = () => {
    const formConfig = loadReferenceFormLabel(SettlementFormConfig, suscriptionData);
    if (suscriptionData?.id) {
      setFormConfig(formConfig)
    }
  }

  const loadInvoiceTemplate = (payload: LoadInvoiceTemplatePubSubPayload) => {
    const { invoiceTemplate } = payload;

    if (invoiceTemplate?.id) {
      const defaultFormData: SettlementDataType = {
        currencyId: invoiceTemplate.currencyId,
        exenta: invoiceTemplate.exenta
      }
      formRef.current?.setFormValue("multiple", defaultFormData, "multipleData")
    }

    dispatch(setDocumentDiscount(Number(invoiceTemplate?.porcenta_descuento || 0)))
    dispatch(setDocumentExempted(invoiceTemplate?.exenta || false))
    dispatch(loadProducts(invoiceTemplate?.invoiceTemplateProducts as EDocumentProductDataType[]))
  }

  const addClientToDocument = (payload: AddClientToDocumentPubSubPayload) => {
    const { client } = payload;
    setClients([
      ...clients,
      client
    ])
    populateClientData(client)
    formRef.current?.reFetchSelectInput("clientId")
  }

  const onInputUpdate = (name: keyof SettlementFormData, value: any, event?: React.ChangeEvent<any>) => {
    if (name === "clientId") {
      const client = clients.find(c => c.id === value);
      if (client?.id) {
        populateClientData(client);
      }
    }
    if (name === "tipo") {
      setDocumentType(value)
      // El tipo de documento decide si se cobran formas de pago: al pasar a uno que no
      // las captura hay que soltar las que quedaron en el estado del documento.
      if (!shouldCapturePaymentWays(value, noAutoCancelContado)) {
        dispatch(clearPaymentWays())
      }
    }
  }

  const populateClientData = async (client: ClientDataType) => {
    if (client?.id) {

      const clientDefaultDocumentType = `${(client.tipo || 0)}`;
      const clientData: SettlementDataType = {
        clientId: client.id,
        ...resolveDocumentClientNames(client),
        plazo: client.plazo || 0,
        tel1: client.telefono1,
        tel1_cd: client.telefono1_cd,
        tel2: client.telefono2,
        tel2_cd: client.telefono2_cd,
        email: client.email,
        tipo_identificacion: client.tipo_identificacion,
        identificacion: client.identificacion,
        direccion: stripHtmlTags(client.direccion || ""),
        tipo: clientDefaultDocumentType
      }

      setDocumentType(clientDefaultDocumentType)
      formRef.current?.setFormValue("multiple",
        {
          ...clientData,
          client_code: client.codigo
        }
        , "multipleData");
      let descuento = Number(client.descuento || 0);
      if (isNaN(descuento)) {
        descuento = 0;
      }

      dispatch(setDocumentDiscount(descuento))
      dispatch(setDocumentDiscountType(client.tipo_descuento || ON_INVOICE_DISCOUNT_TYPE_CODE))

      if (suscriptionData?.pricemanaged === FOUR_PRICES_MANAGEMENT_CODE) {
        let priceCode = Number(client.priceId || 1);
        if (isNaN(priceCode)) {
          priceCode = 1
        }
        dispatch(setPriceCode(priceCode))
      } else {
        if (typeof client.price == "function") {
          const { data } = await client.price()
          let priceData = removeFunctionFromObject(data)
          dispatch(setPrice(priceData))
        }
      }
    }
  }

  const onInputKeyDown = (name: keyof SettlementFormData | "client_code", event?: React.KeyboardEvent<any>) => {
    if (name === "client_code") {
      if (event?.key == "Enter") {
        event?.preventDefault();
        event?.stopPropagation();
        const input = event.target as HTMLInputElement
        selectClientByCode(input.value)
      }
    }
  }

  const selectClientByCode = async (code: string) => {
    const client = clients.find(c => c.codigo === code);
    if (client?.id) {
      formRef.current?.setFormValue("clientId", client.id, "String");
    } else {
      showAlert("danger", "No existe ningun cliente con ese codigo");
    }
  }

  const addPaymentWayToDocument = (data: EDocumentPaymentWayDataType) => {
    dispatch(addPaymentWay(data))
  }

  const onAddExemption = (exemptionChange?: ProductExemptionDataType) => {
    if (exemptionChange?.productId) {
      dispatch(setProductExemptionData({
        productId: `${exemptionChange?.productId}`,
        exemption: exemptionChange
      }))
    } else if (exemptionChange) {
      dispatch(setAllProductsExemptionData({
        exemption: exemptionChange
      }))
      dispatch(setDefaultExemption(exemptionChange))
    }
  }

  const preLoadSettlementData = async (clients: ClientDataType[]) => {
    if (preLoadedSettlementId) {
      let settlement = await SettlementApi.get(preLoadedSettlementId, {
        selectionSet: [
          ...SettlementFormDataKeys.filter(k => !SettlementRelatedAttributes.includes(k)),
          "currency.*",
          "client.*",
          "settlementProducts.*",
          "settlementProducts.product.*"
        ]
      })
      const formData = omitProperties(settlement, ["numero", "id", "createdAt", "updatedAt", "recibeId"], true);
      const client = clients.find(c => c.id == formData.clientId);
      formRef.current?.resetFormValue({
        ...formData,
        client_code: client?.codigo
      })
      dispatch(setDocumentComments(`${formData.document_comments || ""}`))

      if (settlement.settlementProducts) {
        const settlementProducts = settlement.settlementProducts.map(ip => {
          return omitProperties(ip, ["id", "settlementId", "createdAt", "updatedAt"], true);
        })
        dispatch(loadProducts(settlementProducts as EDocumentProductDataType[]))
      }
      if (settlement.currency && settlement.currencyId) {
        CurrencyIdPubSub.publish({ currencyId: settlement.currencyId })
      }
    }
  }

  const loadDefaultData = async (resetLoad = false) => {
    try {
      const { items: allClients } = await ClientApi.fetchAll();
      setClients(allClients);
      if (copyData && !resetLoad) {
        await preLoadSettlementData(allClients)
      } else {
        dispatch(clearDocument())
        let defaultData: SettlementDataType = {}
        const defaultPrice = await PriceApi.getDefaultPrice()

        if (typeof defaultPrice === 'number') {
          dispatch(setPriceCode(defaultPrice))
        } else {
          let priceData = removeFunctionFromObject(defaultPrice)
          dispatch(setPrice(priceData))
        }
        const defaultCurrency = await CurrencyApi.getDefaultCurrency()
        if (defaultCurrency.id) {
          dispatch(setCurrency(removeFunctionFromObject(defaultCurrency)))
          defaultData["currencyId"] = defaultCurrency.id
          if (defaultCurrency.exchange_rate) {
            defaultData["exchange_rate"] = defaultCurrency.exchange_rate
          }
          CurrencyIdPubSub.publish({ currencyId: defaultCurrency.id })
        }

        formRef.current?.setFormValue("multiple", defaultData, "multipleData")
        setDefaultData(defaultData)
        if (resetLoad) {
          formRef.current?.resetFormValue(defaultData)
        }
        const defaultClient = (allClients as ClientDataType[]).find(c => c.is_default);
        if (defaultClient && defaultClient.id) {
          setTimeout(() => {
            formRef.current?.setFormValue("clientId", defaultClient.id, "select");
          });
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const createSettlement = async (formData: SettlementDataType): Promise<SettlementDataType> => {
    const eDocumentState = flushDocumentProductEdits();
    const documentSummaryState = selectEDocumentSummary(eDocumentState);
    let settlementData: SettlementDataType = {}
    const { documentProducts, documentDiscountType, documentPaymentWays, isDocumentExempted } = eDocumentState

    if (documentProducts?.length == 0) {
      throw Error("Por favor agregue algun producto a la factura")
    }

    // Con no_auto_cancel_contado el contado no se cobra al liquidar: no se piden ni se
    // guardan formas de pago, el documento nace pendiente y se cancela con un Recibo.
    const capturePayments = shouldCapturePaymentWays(formData.tipo, noAutoCancelContado)

    const totalPaymentWays = documentPaymentWays?.reduce((a, p) => a + Number(p.monto || 0), Number(0)) || 0;
    if (capturePayments) {
      if (totalPaymentWays < documentSummaryState.total) {
        throw Error("Las formas de pago no cubren el monto del documento")
      }
    }

    let pagada = false;
    let status = "pendiente";
    if (formData.tipo == INVOICE_CONTADO_TYPE_CODE) {
      if (!noAutoCancelContado) {
        pagada = true;
        status = "pagada";
      }
    }
    if (!documentCurrency) {
      throw Error("Por favor Selecciona una moneda")
    }

    settlementData = {
      ...formData,
      ...settlementData,
      subtotal: documentSummaryState.subtotal,
      descuento: documentDiscountType == ON_INVOICE_DISCOUNT_TYPE_CODE ? documentSummaryState.descuento : 0,
      impuestos: documentSummaryState.impuestos,
      total: documentSummaryState.total,
      currencyId: documentCurrency.currencyApiId,
      fecha: getCurrentDateTime(),
      exenta: isDocumentExempted,
      pagada,
      status: status,
      anulada: false,
      porcenta_descuento: eDocumentState.documentDiscount,
      document_comments: eDocumentState.documentComments
    }

    if (documentProducts) {
      const { reduce_stock_on_settlement = false } = suscriptionData
      const settlementPayments = capturePayments ? generateSettlementPayments(documentPaymentWays, settlementData) : [];
      const createdSettlement = await SettlementApi.createSettlementAndProducts(settlementData, documentProducts as SettlementProductDataType[], settlementPayments, reduce_stock_on_settlement)
      clearForm()
      showAlert("success", "La Liquidacion ha sido creada existosamente")
      ConfirmPrintPubSub.publish({
        openOffCanvas: true,
        printVersionUrl: `/user/settlements/print/${createdSettlement.id}/0/1`,
      })
      return createdSettlement;
    }

    return {} as SettlementDataType;
  }

  const generateSettlementPayments = (payment: EDocumentPaymentWayDataType[] = [], settlementData: SettlementDataType): SettlementPaymentDataType[] => {
    const paymentTotal = payment.reduce((a, v) => a + Number(v.monto || 0), Number(0))
    let moneyBack = paymentTotal - Number(documentSummary?.total || 0)

    return payment.map(p => {
      let vuelto = Number(0)
      if (p.forma_pago == `${CASH_PAYMENT_WAY_CODE}`) {
        vuelto = moneyBack
        moneyBack = 0
      }
      const settlementPayment: SettlementPaymentDataType = {
        descripcion: `Pago Liquidacion #${settlementData.numero}`,
        monto: Number(p.monto),
        vuelto: vuelto,
        formaPago: `${p.forma_pago}`,
        digitosTarjeta: p.digitosTarjeta,
        numAprobacion: p.numAprobacion,
        exchangeRate: settlementData.exchange_rate,
        created: settlementData.fecha,
        currencyId: settlementData.currencyId,
        bankAccountId: p.bankAccountId,
        creditOperatorId: p.creditOperatorId
      }
      return settlementPayment
    })

  }

  const clearForm = async () => {
    dispatch(clearDocument())
    loadDefaultData(true)
  }

  const getCreateConfirmation = (): ConfirmationConfig | undefined => {
    const { ask_confirm = false } = suscriptionData || {}
    if (ask_confirm) {
      return {
        message: "Esta seguro que desea crear la Liquidacion"
      }
    }
  }

  const onClientSelected = (clientData?: ClientDataType) => {
    const clientInvoiceData: SettlementDataType = {
      clientActivitycode: clientData?.activitycode,
      ...resolveDocumentClientNames(clientData || {}),
      identificacion: clientData?.identificacion,
      tipo_identificacion: clientData?.tipo_identificacion
    }
    formRef.current?.setFormValue("multiple", clientInvoiceData, "multipleData")
  }

  return (
    <>
      {formConfig?.sections && (<GenericForm<SettlementFormData, SettlementDataType>
        ref={formRef}
        formId={props.formId}
        defaultData={defaultData}
        modelRelativeObjects={SettlementRelatedAttributes}
        onInputChange={onInputUpdate}
        confirmationConfig={getCreateConfirmation()}
        onInputKeyDown={onInputKeyDown}
        formConfig={formConfig}
        getById={(...arg) => SettlementApi.get(...arg)}
        createFn={(...arg) => createSettlement(...arg)}
      >
        <Row>
          <Col>
            <h3>
              Productos
            </h3>
          </Col>
          <Col xs="1">
            <PriceSelector />
          </Col>
        </Row>
        <QuickAddProductForm />
        <EdocumentProductList />
        <EdocumentComments />
      </GenericForm >
      )}

      <HiddenableContainer show={capturePaymentWays}>
        <Row>
          <Col md={{
            size: 10,
            offset: 1
          }}>
            <hr />
            <h3 className="mb-4">
              Agregar Forma de Pago
            </h3>
            <DocumentPaymentWaysForm innerForm={true} onPaymentWayAdded={addPaymentWayToDocument} />
            <hr className="mt-4 mb-4" />
            <EdocumentPaymentWayList documentSummary={documentSummary} />
          </Col>
        </Row>
      </HiddenableContainer>
      <DocumentExemptionOffCanvas onAddExemption={onAddExemption} />
      <LoadingScreen isLoading={isLoading} />
      <DocumentPrintConfirmation
        title="Imprimir Liquidacion"
      />
      <SearchClientByIdentificationOffCanvas
        onClientSelected={onClientSelected}
      />
    </>

  )
}

export default SettlementForm;